import { getAccessToken, ensureFreshAccessToken } from './authStore';
import { safeGetSession } from './supabase';
import {
  pickDriveFolder,
  pickDriveFile,
  PickedDriveFolder,
  PickedDriveFile,
} from './googleDrivePicker';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

if (!apiBaseUrl) {
  throw new Error(
    'Missing REACT_APP_API_BASE_URL. Set it to your API Gateway base URL, e.g. https://.../dev/api'
  );
}

export interface GoogleDriveStatus {
  connected: boolean;
  configured: boolean;
  email?: string | null;
  connectedAt?: string | null;
  message?: string;
}

export interface DriveDestinationFolder {
  id: string;
  name: string;
}

export interface DriveDestinations {
  recentFolders: DriveDestinationFolder[];
  pickerConfigured: boolean;
}

export interface LinkedDriveFile {
  id: string;
  file_name: string;
  mime_type?: string | null;
  source?: string;
  drive_file_id?: string | null;
  drive_web_view_link?: string | null;
  created_at?: string;
}

async function getJwtOrThrow(): Promise<string> {
  const token =
    (await ensureFreshAccessToken()) ||
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('Ta session a expiré. Reconnecte-toi et réessaie.');
  }
  return token;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const token = await getJwtOrThrow();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };
  headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(input, { ...init, headers });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    let code: string | undefined;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
      if (typeof body?.code === 'string') code = body.code;
    } catch {
      // ignore
    }
    const err = new Error(message) as Error & { status?: number; code?: string };
    err.status = res.status;
    if (code) err.code = code;
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const googleDriveService = {
  async getStatus(): Promise<GoogleDriveStatus> {
    return jsonFetch<GoogleDriveStatus>(`${apiBaseUrl}/integrations/google/drive/status`);
  },

  async getConnectUrl(): Promise<string> {
    const data = await jsonFetch<{ url: string }>(
      `${apiBaseUrl}/integrations/google/drive/connect`
    );
    if (!data?.url) throw new Error('Missing connect URL');
    return data.url;
  },

  async disconnect(): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/drive/disconnect`, {
      method: 'POST',
      body: '{}',
    });
  },

  async getDestinations(): Promise<DriveDestinations> {
    return jsonFetch<DriveDestinations>(
      `${apiBaseUrl}/integrations/google/drive/destinations`
    );
  },

  async getPickerToken(): Promise<{
    accessToken: string;
    expiresIn: number;
    clientId: string;
    apiKey: string;
    appId?: string;
  }> {
    return jsonFetch(`${apiBaseUrl}/integrations/google/drive/picker-token`, {
      method: 'POST',
      body: '{}',
    });
  },

  async pickFolder(title?: string): Promise<PickedDriveFolder | null> {
    const token = await this.getPickerToken();
    return pickDriveFolder(token, title);
  },

  async pickFile(title?: string): Promise<PickedDriveFile | null> {
    const token = await this.getPickerToken();
    return pickDriveFile(token, title);
  },

  async exportFile(
    fileId: string,
    folderId: string
  ): Promise<{ driveFileId: string; webViewLink: string; folderId?: string }> {
    const data = await jsonFetch<{
      ok?: boolean;
      driveFileId: string;
      webViewLink: string;
      folderId?: string;
    }>(`${apiBaseUrl}/integrations/google/drive/export`, {
      method: 'POST',
      body: JSON.stringify({ fileId, folderId }),
    });
    if (!data?.webViewLink || !data?.driveFileId) {
      throw new Error('Missing Drive export result');
    }
    return {
      driveFileId: data.driveFileId,
      webViewLink: data.webViewLink,
      folderId: data.folderId,
    };
  },

  async linkFile(
    projectId: string,
    driveFileId: string
  ): Promise<{ file: LinkedDriveFile; alreadyLinked: boolean }> {
    const data = await jsonFetch<{
      ok?: boolean;
      alreadyLinked?: boolean;
      file: LinkedDriveFile;
    }>(`${apiBaseUrl}/integrations/google/drive/link`, {
      method: 'POST',
      body: JSON.stringify({ projectId, driveFileId }),
    });
    if (!data?.file?.id) {
      throw new Error('Missing Drive link result');
    }
    return {
      file: data.file,
      alreadyLinked: !!data.alreadyLinked,
    };
  },

  async resolveFile(
    fileId: string
  ): Promise<{
    available: boolean;
    webViewLink?: string;
    cleared?: boolean;
    deleted?: boolean;
  }> {
    return jsonFetch(`${apiBaseUrl}/integrations/google/drive/resolve`, {
      method: 'POST',
      body: JSON.stringify({ fileId }),
    });
  },
};
